/**
 * BookmarkPopup — names a bookmark, mirroring the light (FlashEvent) popup.
 *
 * Opens when a bookmark marker is clicked or one is placed (store.editing-
 * BookmarkBeat). A bookmark only has a name, so the popup is a single text field
 * (autofocused so you can type the name straight away) plus Delete. Draggable by
 * its header like the other popups; close with ×, Escape, or by clicking the
 * marker again.
 */

import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font } from '../ui/theme';
import { IcTrash, IcBookmark } from '../ui/icons';
import { useDraggablePanel } from '../ui/useDraggablePanel';
import { isSynthVrView } from '../../store/editorSettings';
import { BOOKMARK_COLORS, bookmarkColorHex } from './bookmarkColors';

export default function BookmarkPopup() {
  const editingBookmarkBeat = useEditorStore(s => s.editingBookmarkBeat);
  const bookmarks = useEditorStore(s => s.bookmarks);
  const updateBookmarkName = useEditorStore(s => s.updateBookmarkName);
  const setBookmarkColor = useEditorStore(s => s.setBookmarkColor);
  const vr = useEditorStore(s => isSynthVrView(s.activeView));
  const removeBookmark = useEditorStore(s => s.removeBookmark);
  const setEditingBookmarkBeat = useEditorStore(s => s.setEditingBookmarkBeat);
  const { panelRef, headerProps, draggedStyle } = useDraggablePanel();
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => setEditingBookmarkBeat(null);

  useEffect(() => {
    if (editingBookmarkBeat == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditingBookmarkBeat(null); };
    window.addEventListener('keydown', onKey);
    // Synth VR: a click anywhere outside the panel closes it. Judged at click time, after the scene's
    // own handlers: a click that toggled or switched the bookmark has already changed the store, so it
    // is left alone (otherwise clicking the badge to close it, or another badge, would fight this).
    let downOutside = false;
    const onDown = (e: PointerEvent) => { downOutside = !panelRef.current?.contains(e.target as Node); };
    const onClick = () => {
      const st = useEditorStore.getState();
      if (downOutside && isSynthVrView(st.activeView) && st.editingBookmarkBeat === editingBookmarkBeat) st.setEditingBookmarkBeat(null);
      downOutside = false;
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('click', onClick);
    // Focus the name field so the user can type the label immediately.
    const id = requestAnimationFrame(() => { inputRef.current?.focus(); inputRef.current?.select(); });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('click', onClick);
      cancelAnimationFrame(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBookmarkBeat, setEditingBookmarkBeat]);

  if (editingBookmarkBeat == null) return null;
  const bm = bookmarks.find(b => Math.abs(b.beat - editingBookmarkBeat) < 0.01);
  if (!bm) return null;

  const del = () => { removeBookmark(bm.beat); close(); };

  return (
    <div ref={panelRef} style={{ ...panelStyle, ...draggedStyle }}>
      <div style={{ ...headerStyle, ...headerProps.style }} onPointerDown={headerProps.onPointerDown}>
        <span style={titleStyle}>
          <IcBookmark size={15} style={{ color: '#fff' }} />
          Bookmark · beat {bm.beat.toFixed(1)}
        </span>
        <button style={closeBtnStyle} onClick={close} title="Close (Esc)">×</button>
      </div>

      <input
        ref={inputRef}
        style={inputStyle}
        value={bm.name}
        placeholder="Name this bookmark…"
        onChange={e => updateBookmarkName(bm.beat, e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') close(); }}
      />

      {/* Synth VR only: the Unity editor's 15 bookmark colours. Picking one colours the bookmark on the
          track and its circle on the waveform. */}
      {vr && (
        <div role="radiogroup" aria-label="Bookmark colour" style={swatchGridStyle}>
          {BOOKMARK_COLORS.map(c => {
            const selected = (bm.color ?? 0) === c.value;
            return (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={selected}
                title={c.name}
                data-testid={`bookmark-color-${c.value.toString(16).padStart(6, '0')}`}
                onClick={() => setBookmarkColor(bm.beat, c.value)}
                style={{ ...swatchStyle, background: bookmarkColorHex(c.value)!, ...(selected ? swatchSelectedStyle : {}) }}
              />
            );
          })}
        </div>
      )}

      <button style={deleteBtnStyle} onClick={del} data-hover-danger><IcTrash size={13} /> Delete bookmark</button>
    </div>
  );
}

// Matches the light popup's themed card.
const panelStyle: React.CSSProperties = {
  position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)', zIndex: 60,
  width: 'min(280px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 90px)', overflowY: 'auto',
  padding: 14, borderRadius: 16,
  background: 'oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
  display: 'flex', flexDirection: 'column', gap: 11, fontFamily: font.sans,
};
const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 };
const titleStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, color: color.ink, fontSize: 13, fontWeight: 700, letterSpacing: 0.2 };
const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '9px 13px', borderRadius: 999,
  background: 'oklch(0.14 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.ink, fontFamily: font.sans, fontSize: 13, outline: 'none',
};
// 15 swatches in a 5 × 3 grid, the order of the Unity selector.
const swatchGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, justifyItems: 'center' };
const swatchStyle: React.CSSProperties = {
  width: 28, height: 28, borderRadius: '50%', padding: 0, cursor: 'pointer',
  border: '1px solid rgba(255, 255, 255, 0.25)',
};
const swatchSelectedStyle: React.CSSProperties = {
  border: '2px solid #fff', boxShadow: '0 0 0 2px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h))',
};
const deleteBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.35)',
  color: '#F87171', fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
